import { docxClient } from "../clients/docx.client";
import type { ReportDocumentProps } from "../../hooks/useReportPDF";
import { REPORT_CONSTANTS } from "../../config/constants";
import { extractCombinedData } from "../../helpers";
import { parseFilenameFromContentDisposition } from "~/helpers/parse-content-disposition.helper";

export interface GeneratedDocument {
  url: string;
  filename?: string;
}

interface SubmitDocxJobResponse {
  requestId: string;
}

interface DocxJobStatusResponse {
  status: "pending" | "completed" | "error";
  detail?: string;
}

// See pdf.gateway.ts's identical constants for why: the backend enqueues and
// replies 202 immediately instead of blocking on docx-service, so polling
// replaces the old synchronous wait with no shared resource held open.
const DOCX_POLL_INTERVAL_MS = 3000;
const DOCX_POLL_MAX_WAIT_MS = 20 * 60 * 1000;

class DOCXGateway {
  private async pollUntilReady(requestId: string): Promise<void> {
    const deadline = Date.now() + DOCX_POLL_MAX_WAIT_MS;
    while (Date.now() < deadline) {
      const { data } = await docxClient.get<DocxJobStatusResponse>(`/docx/status/${requestId}`);
      if (data.status === 'completed') return;
      if (data.status === 'error') throw new Error(data.detail || 'Falha ao gerar DOCX.');
      await new Promise((resolve) => setTimeout(resolve, DOCX_POLL_INTERVAL_MS));
    }
    throw new Error('Tempo limite excedido aguardando a geração do DOCX.');
  }

  async generateDOCX(props: ReportDocumentProps): Promise<GeneratedDocument> {
    try {
      const reportType = props.metadata?.[REPORT_CONSTANTS.new_report.report_type];
      const isCombinado = reportType === REPORT_CONSTANTS.types.combinado;
      const baseBody = isCombinado ? extractCombinedData(props) : props; // Caso seja combinado, filtrar para enviar apenas combined_data
      const docxPayload: ReportDocumentProps =
        props.executive_summary !== undefined
          ? { ...baseBody, executive_summary: props.executive_summary }
          : baseBody;

      const { data: job } = await docxClient.post<SubmitDocxJobResponse>('/create/docx', docxPayload);
      await this.pollUntilReady(job.requestId);

      const response = await docxClient.get(`/docx/download/${job.requestId}`, {
        responseType: 'blob'
      });

      const docxBlob = response.data;
      const url = URL.createObjectURL(docxBlob);
      const filename = parseFilenameFromContentDisposition(
        response.headers?.['content-disposition'] ?? response.headers?.['Content-Disposition'],
      );

      console.log('DOCX generated successfully by service');
      return { url, filename };
    } catch (error) {
      console.error('Error calling DOCX service:', error);
      throw error;
    }
  }
}

export const docxGateway = new DOCXGateway();